/**
 * DocViewStateService — localStorage-backed per-doc view state.
 *
 * Remembers mode, scroll position, edgeless viewport, and fullscreen state
 * across doc navigation AND browser restarts.
 *
 * - Backed by localStorage (survives reload, tab close, browser restart)
 * - LRU eviction: max 50 entries, 7-day TTL
 * - Single source of truth for ephemeral view state
 * - primaryMode (docProperties/Y.js) and URL params are independent
 */
import type { DocMode } from '@blocksuite/affine/model';
import { Service } from '@toeverything/infra';

import { DEFAULT_DOC_MODE } from '../../doc/constants';

export interface FullscreenState {
  viewId: string;
  blockId: string;
  databaseViewId?: string;
}

export interface DocViewState {
  mode: DocMode;
  page: { scrollTop: number } | null;
  edgeless: { centerX: number; centerY: number; zoom: number } | null;
  fullscreen: FullscreenState | null;
  lastAccessed: number;
}

const STORAGE_KEY = 'sl:doc-view-states';
const MAX_ENTRIES = 50;
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export class DocViewStateService extends Service {
  private readonly states: Map<string, DocViewState>;

  constructor() {
    super();
    this.states = this.load();
  }

  get(docId: string): DocViewState | null {
    const entry = this.states.get(docId);
    if (!entry) return null;

    if (Date.now() - entry.lastAccessed > TTL_MS) {
      this.states.delete(docId);
      this.persist();
      return null;
    }

    // Touch: move to end of Map insertion order (LRU)
    entry.lastAccessed = Date.now();
    this.states.delete(docId);
    this.states.set(docId, entry);
    // No persist here — touch is cheap, persisted on next set/update

    return entry;
  }

  set(docId: string, state: Omit<DocViewState, 'lastAccessed'>): void {
    this.states.delete(docId);

    if (this.states.size >= MAX_ENTRIES) {
      const oldestKey = this.states.keys().next().value;
      if (oldestKey) {
        this.states.delete(oldestKey);
      }
    }

    this.states.set(docId, { ...state, lastAccessed: Date.now() });
    this.persist();
  }

  update(
    docId: string,
    patch: Partial<Omit<DocViewState, 'lastAccessed'>>
  ): void {
    const existing = this.get(docId);
    if (existing) {
      this.set(docId, { ...existing, ...patch });
    } else {
      this.set(docId, {
        mode: patch.mode ?? DEFAULT_DOC_MODE,
        page: patch.page ?? null,
        edgeless: patch.edgeless ?? null,
        fullscreen: patch.fullscreen ?? null,
      });
    }
  }

  remove(docId: string): void {
    this.states.delete(docId);
    this.persist();
  }

  clearFullscreen(docId: string): void {
    const entry = this.get(docId);
    if (entry) {
      this.set(docId, { ...entry, fullscreen: null });
    }
  }

  // ─── localStorage persistence ───

  private persist(): void {
    try {
      const entries = Array.from(this.states.entries());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // localStorage full or unavailable — silently ignore
    }
  }

  private load(): Map<string, DocViewState> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return new Map();

      const entries: [string, DocViewState][] = JSON.parse(raw);
      const now = Date.now();
      const valid = entries.filter(([, v]) => now - v.lastAccessed < TTL_MS);

      // Keep only the newest MAX_ENTRIES
      return new Map(valid.slice(-MAX_ENTRIES));
    } catch {
      return new Map();
    }
  }
}
